/**
 * Code Block Component
 * Renders code with syntax highlighting and copy button
 */

import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CodeBlockProps {
  children: string;
  className?: string;
  inline?: boolean;
}

export const CodeBlock: React.FC<CodeBlockProps> = ({ 
  children, 
  className,
  inline = false 
}) => {
  const [copied, setCopied] = useState(false);
  
  // Extract language from className (format: language-xxx)
  const match = /language-(\w+)/.exec(className || '');
  const language = match ? match[1] : 'text';
  
  // Inline code - use break-all to force wrapping at any character for long strings
  if (inline) {
    return (
      <code
        className="px-1.5 py-0.5 rounded bg-muted text-sm font-mono inline-block max-w-full"
        style={{ wordBreak: 'break-all' }}
      >
        {children}
      </code>
    );
  }

  // Handle copy
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // console.error('Failed to copy:', err);
    }
  };

  // In chat context, always use simple pre with wrapping - SyntaxHighlighter causes overflow issues
  return (
    <div className="relative group my-4 rounded-lg border" style={{ maxWidth: '100%' }}>
      {/* Language label */}
      <div className="flex items-center justify-between px-4 py-2 bg-muted border-b">
        <span className="text-xs font-medium text-muted-foreground uppercase">
          {language}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
          onClick={handleCopy}
        >
          {copied ? (
            <>
              <Check className="h-3 w-3 mr-1" />
              <span className="text-xs">Copied!</span>
            </>
          ) : (
            <>
              <Copy className="h-3 w-3 mr-1" />
              <span className="text-xs">Copy</span>
            </>
          )}
        </Button>
      </div>

      {/* Code content - simple pre with wrapping for chat context */}
      <pre
        className="p-4 text-sm font-mono bg-muted/30"
        style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', overflowWrap: 'anywhere' }}
      >
        {children}
      </pre>
    </div>
  );
};
